import { useState } from 'react';
import { Alert, Button, Icon, Label, Select, Spinner } from '../atoms';
import { FormField } from '../molecules';
import type { RegisterCredentials } from '../../types/auth';

interface RegisterFormProps {
  onRegister: (credentials: RegisterCredentials) => Promise<void>;
  onSwitchToLogin: () => void;
  isLoading: boolean;
  error: string | null;
}

// Common country codes and phone codes
const countries = [
  { code: 'US', name: 'United States', phoneCode: '+1' },
  { code: 'CA', name: 'Canada', phoneCode: '+1' },
  { code: 'GB', name: 'United Kingdom', phoneCode: '+44' },
  { code: 'AU', name: 'Australia', phoneCode: '+61' },
  { code: 'DE', name: 'Germany', phoneCode: '+49' },
  { code: 'FR', name: 'France', phoneCode: '+33' },
  { code: 'ES', name: 'Spain', phoneCode: '+34' },
  { code: 'IT', name: 'Italy', phoneCode: '+39' },
  { code: 'NL', name: 'Netherlands', phoneCode: '+31' },
  { code: 'BR', name: 'Brazil', phoneCode: '+55' },
  { code: 'MX', name: 'Mexico', phoneCode: '+52' },
  { code: 'IN', name: 'India', phoneCode: '+91' },
  { code: 'JP', name: 'Japan', phoneCode: '+81' },
  { code: 'KR', name: 'South Korea', phoneCode: '+82' },
  { code: 'CN', name: 'China', phoneCode: '+86' },
];

export function RegisterForm({ onRegister, onSwitchToLogin, isLoading, error }: RegisterFormProps) {
  const [formData, setFormData] = useState<RegisterCredentials>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    dateOfBirth: '',
    gender: 'other',
    phoneNumber: '',
    countryCode: 'US',
    phoneCode: '+1',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const handleInputChange = (field: keyof RegisterCredentials, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCountryChange = (countryCode: string) => {
    const country = countries.find(c => c.code === countryCode);
    if (country) {
      setFormData(prev => ({
        ...prev,
        countryCode: country.code,
        phoneCode: country.phoneCode,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    
    if (formData.password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }
    
    await onRegister(formData);
  };

  return (
    <div className="backdrop-blur-sm bg-white/95 border border-white/20 rounded-2xl shadow-2xl p-8 transform transition-all duration-300 hover:shadow-3xl animate-[fadeIn_0.6s_ease-out]">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-text-primary mb-2">Create Account</h2>
        <p className="text-text-secondary text-sm">Join Treel and start your AI coaching journey</p>
      </div>
      
      {(error ?? passwordError) && (
        <Alert 
          variant="error" 
          message={error ?? passwordError ?? ''}
          className="mb-6"
        />
      )}
      
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            id="firstName"
            type="text"
            label="First Name"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            placeholder="Enter your first name"
            required
            leftIcon={<Icon name="user" size="sm" />}
          />

          <FormField
            id="lastName"
            type="text"
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            placeholder="Enter your last name"
            required
            leftIcon={<Icon name="user" size="sm" />}
          />
        </div>
        
        {/* Email */}
        <FormField
          id="email"
          type="email"
          label="Email Address"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          placeholder="Enter your email"
          required
          leftIcon={<Icon name="email" size="sm" />}
        />

        {/* Personal Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            id="dateOfBirth"
            type="date"
            label="Date of Birth"
            value={formData.dateOfBirth}
            onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
            required
            leftIcon={<Icon name="calendar" size="sm" />}
          />

          <div className="space-y-1">
            <Label htmlFor="gender" required>
              Gender
            </Label>
            <Select
              id="gender"
              value={formData.gender}
              onChange={(value) => handleInputChange('gender', value as 'male' | 'female' | 'other')}
              options={[
                { value: 'other', label: 'Prefer not to say' },
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' }
              ]}
              leftIcon={<Icon name="user" size="sm" />}
              required
            />
          </div>
        </div>

        {/* Country and Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="country" required>
              Country
            </Label>
            <Select
              id="country"
              value={formData.countryCode}
              onChange={handleCountryChange}
              options={countries.map(country => ({
                value: country.code,
                label: `${country.name} (${country.phoneCode})`
              }))}
              leftIcon={<Icon name="settings" size="sm" />}
              required
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="phoneNumber" required>
              Phone Number
            </Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-border-light bg-bg-surface text-text-secondary text-sm">
                <Icon name="user" size="xs" className="mr-2" />
                {formData.phoneCode}
              </span>
              <input
                type="tel"
                id="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                placeholder="Enter your phone number"
                className="flex-1 px-3 py-2 border border-border-light rounded-r-md bg-bg-primary text-text-primary focus:border-action-key-default focus:ring-1 focus:ring-action-key-default focus:outline-none"
                required
              />
            </div>
          </div>
        </div>
        
        {/* Password Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            id="password"
            type="password"
            label="Password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            placeholder="Min 8 characters"
            required
            leftIcon={<Icon name="password" size="sm" />}
          />

          <FormField
            id="confirmPassword"
            type="password"
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your password"
            required
            leftIcon={<Icon name="check" size="sm" />}
          />
        </div>
        
        <div className="pt-4">
          <Button
            type="submit"
            variant="primary"
            size="large"
            state={isLoading ? 'disabled' : 'enabled'}
            fullWidth
          >
            <div className="flex items-center justify-center">
              {isLoading ? (
                <>
                  <Spinner size="sm" variant="white" className="mr-3" />
                  Creating account...
                </>
              ) : (
                <>
                  <Icon name="plus" size="sm" className="mr-2" />
                  Create Account
                </>
              )}
            </div>
          </Button>
        </div>
      </form>
      
      <div className="mt-8">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border-light" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-text-muted">Already have an account?</span>
          </div>
        </div>
        
        <div className="mt-6 text-center">
          <Button
            onClick={onSwitchToLogin}
            variant="tertiary"
            size="medium"
            fullWidth
          >
            <div className="flex items-center justify-center">
              <Icon name="logout" size="sm" className="mr-2" />
              Sign In Instead
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
}